/**
 * Federation `allow_alias` (migration 20261004160000). When a federation turns
 * aliases off, a player's alias reads as unset, so every name ladder
 * (`participantLabel.ts` and its copies, the app's `resolveDisplayName`) falls
 * through to the real name. The gate is applied where rows are loaded, so the
 * ladders themselves never learn about the flag. The stored alias is untouched:
 * switching the flag back on restores it.
 *
 * The rule itself lives in SQL (`alias_allowed_for_event` /
 * `alias_allowed_for_player`); these helpers only apply its answer.
 */

type WithAlias = { alias?: string | null };

/** The player with its alias blanked when aliases are not allowed. */
export function gateAlias<T extends WithAlias | null | undefined>(
  player: T,
  aliasAllowed: boolean,
): T {
  if (aliasAllowed || !player || !player.alias) return player;
  return { ...player, alias: null };
}

/**
 * Blank the alias of the players embedded under `keys` on each row.
 * `isAllowed` is only asked when some row actually carries an alias, so the
 * common case (nobody has one) costs no extra round trip.
 */
export async function gateRowAliases<R>(
  rows: R[],
  keys: readonly (keyof R)[],
  isAllowed: () => Promise<boolean>,
): Promise<R[]> {
  const hasAlias = rows.some((row) => keys.some((key) => !!(row[key] as WithAlias | null)?.alias));
  if (!hasAlias || (await isAllowed())) return rows;

  return rows.map((row) => {
    const gated = { ...row };
    for (const key of keys) {
      gated[key] = gateAlias(row[key] as WithAlias | null, false) as R[keyof R];
    }
    return gated;
  });
}
